/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue';
import axios from 'axios'

export default {
  API: {
    AUTH_TOKEN: '/access-service/token',
    AUTH_ACCESS_TOKEN: '/access-service/token',
    AUTH_REFRESH: '/access-service/token',
    AUTH_PROFILE: '/access-service/user/profile',
    AUTH_REQUEST_PASSWORD: '/access-service/password/requestReset',
    AUTH_REQUEST_PASSWORD_VALIDATE: '/access-service/password/validateResetPassword',
    AUTH_REQUEST_PASSWORD_RESET: '/access-service/password/reset',
    AUTH_REQUEST_EXPIRED_PASSWORD_UPDATE: '/access-service/password/updateExpiredPassword',
    AUTH_REQUEST_FIRST_TIME_PASSWORD_UPDATE: '/access-service/password/firstLoginChangePassword',
    ACCOUNT_PASSWORD_CHANGE: '/access-service/password/change',
    MANAGEMENT_CREATE_PASS_WORD: '/access-service/password/createPassword',
    MANAGEMENT_USER: '/access-service/user',
    MANAGEMENT_PERMISSION: '/access-service/permission',
    MANAGEMENT_ACCESS: '/access-service/access',
    MANAGEMENT_GET_USERS: '/access-service/access/users', 
    MANAGEMENT_VALIDATE_REGISTRATION_LINK: '/access-service/user/validateRegistrationLink',
    MANAGEMENT_RESEND_INVITATION_EMAIL: '/access-service/user/resendInvitationEmail',
    MANAGEMENT_AUTH_REQUEST_PASSWORD_VALIDATE: '/access-service/password/validateResetPassword',
    MANAGEMENT_GLOBAL_CONFIGURATION: '/access-service/app/configuration/key/global',
    MANAGEMENT_APP_CONFIGURATION: '/access-service/app/configuration',

    SETTINGS_COMPANY_PROFILE: '/settings/companyProfileSettings',
    SETTINGS_COMPANY_PROFILE_UPDATE: '/settings/updateCompanyProfileSettings',
    SETTINGS_COMPANY_ALERT_SETTINGS: '/settings/companyAlertSettings',
    SETTINGS_COMPANY_ALERT_SETTINGS_UPDATE: '/settings/updateCompanyAlertSettings',
    SETTINGS_COMPANY_ALERT_RESET: '/settings/resetAlerts',
    SETTINGS_COMPANY_PROMPT: '/settings/companyProfilePrompts',
    SETTINGS_COMPANY_PROMPT_UPDATE: '/settings/updateCompanyProfilePrompts',

    SETTINGS_COMPANY_CONFIG: '/settings/companyConfigSettings',
    UPDATE_COMPANY_CONFIG_SETTING: '/settings/updateCompanyConfigSettings',

    SETTINGS_GLOBAL_CONFIGURATION: '/settings/globalConfiguration',
    SETTINGS_GLOBAL_CONFIGURATION_UPDATE: '/settings/updateGlobalConfiguration',
    SETTINGS_COMPANY_GLOBAL_CONFIGURATION: '/settings/companyGlobalConfiguration',
    SETTINGS_COMPANY_CONFIGURATION: '/settings/companyConfiguration',
    SETTINGS_COMPANY_CONFIGURATION_UPDATE: '/settings/updateCompanyConfiguration',

    C1_Login: '/c1Login',
    C1_AUTH_TOKEN: '/c1Auth',
  },

  cancelTokens: [],

  requestHeaders(authUser) {
    return {
      Authorization: "Bearer " + authUser.accessToken
    }
  },

  getRequest(authUser, url, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;

      if(authUser.config) {
        headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      }
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      return axios.get(url, { headers: headers, timeout: 300000 });
    } else {
      return null;
    }
  },

  postRequest(authUser, url, payload, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      
      if(authUser.config) {
        headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      }
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      let cancelTokenKey = url;
      if (this.cancelTokens[cancelTokenKey]) {
        // cancel request
        this.cancelTokens[cancelTokenKey].cancel();
      }
      this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

      let options = {
        timeout: 300000, // timeout in 5 mins
        headers: headers,
        cancelToken: this.cancelTokens[cancelTokenKey].token,
      };

      return axios.post(url, payload, options);
    } else {
      return null;
    }
  },

  async authToken(form) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_TOKEN;
    let data = {
      email: form.email,
      password: form.password,
      googleToken: form.googleToken,
      emailToken: form.isEmailToken
    }
    //return axios.post(url, data);

    let res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'manual',
      body: JSON.stringify(data)
    });

    if(res.body != null && res.body != undefined) {
      try {
        res.data = await res.json();
      }
      catch(e) {
        console.log(e);
      }
    }

    if(res.status != 200) {
      if(res.data && res.data.errorCode) {
        return res;
      }
      else {
        return {status: 401, data: {errorCode: 5002}};
      }
    }
    return res;
  },

  async authFirebaseToken(token) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_FIREBASE_TOKEN;
    let headers = {Authorization: "Bearer " + token};
    return axios.post(url, null, {headers: headers});
  },

  async getAccessToken(companyId, token) {    
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_ACCESS_TOKEN + "/" + companyId;
    let headers = {Authorization: "Bearer " + token};
    //return axios.post(url, null, {headers: headers});

    let res = await fetch(url, {
      method: 'POST',
      headers: headers,
      redirect: 'manual'
    })
    
    if(res.status == 0 && res.type == "opaqueredirect") {
      return {status: 401, data: {errorCode: 5002}};
    }

    res.data = await res.json();
    return res;
  },

  async refreshAccessToken(token) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_REFRESH;
    let headers = {Authorization: "Bearer " + token};
    //return axios.put(url, null, {headers: headers});

    let res = await fetch(url, {
      method: 'PUT',
      headers: headers,
      redirect: 'manual'
    })

    if(res.status == 0 && res.type == "opaqueredirect") {      
      return {status: 401, data: {errorCode: 5002}};
    }

    res.data = await res.json();
    return res;
  },

  authProfile(token) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_PROFILE;
    let headers = {Authorization: "Bearer " + token};
    return axios.get(url, {headers: headers});
  },

  authRequestPassword(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_REQUEST_PASSWORD;
    return axios.put(url, data);
  },

  authResetPassword(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_REQUEST_PASSWORD_RESET;
    return axios.put(url, data);
  },

  authUpdateExpiredPassword(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_REQUEST_EXPIRED_PASSWORD_UPDATE;
    return axios.put(url, data);
  },

  authUpdateFirstTimePassword(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.AUTH_REQUEST_FIRST_TIME_PASSWORD_UPDATE;
    return axios.put(url, data);
  },

  authRequestPasswordValidate(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_AUTH_REQUEST_PASSWORD_VALIDATE}?userEmail=${encodeURIComponent(data.email)}&uuid=${data.uuid}`;
    return axios.get(url);
  },

  accountChangePassword(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.ACCOUNT_PASSWORD_CHANGE;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  inviteUser(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_USER;
    let headers = this.requestHeaders(authUser);
    return axios.post(url, data, {headers: headers});
  },

  resendInvitationEmail(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_RESEND_INVITATION_EMAIL;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  updateUserInfo(authUser, data, userId) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/${userId}/profile`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  getAccessibleCompanies(authUser) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_ACCESS}/accessibleCompanies`;
    let headers = this.requestHeaders(authUser);
    return axios.get(url, {headers: headers});
  },

  getPermissions(authUser) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_PERMISSION}`;
    let headers = this.requestHeaders(authUser);
    return axios.get(url, {headers: headers});
  },

  getUsers(authUser, accountId) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_GET_USERS}/${accountId}`;
    let headers = this.requestHeaders(authUser);
    return axios.get(url, {headers: headers});
  },

  updateUserAccessList(authUser, data, userId) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/${userId}/accessList`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  updateUserRole(authUser, data, userId) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/${userId}/role`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  createPassword(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_CREATE_PASS_WORD;
    return axios.put(url, data);
  },

  managementValidateRegistrationLink(data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_VALIDATE_REGISTRATION_LINK}?userEmail=${encodeURIComponent(data.email)}&userId=${data.userId}`;
    return axios.get(url);
  },

  updateUserStatus(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/status`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  updateUserType(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/userType`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  updateDefaultCompany(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/updateDefaultCompany`;
    let headers = this.requestHeaders(authUser);
    return axios.put(url, data, {headers: headers});
  },

  deleteUser(authUser, userId) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + `${this.API.MANAGEMENT_USER}/${userId}`;
    let headers = this.requestHeaders(authUser);
    return axios.delete(url, {headers: headers});
  },

  async fetchGlobalConfiguration() {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_GLOBAL_CONFIGURATION;
    return axios.get(url);
  },

  async fetchCompanyConfiguration({token, companyId}) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_APP_CONFIGURATION + `/${companyId}`;
    let headers = {Authorization: "Bearer " + token};
    return axios.get(url, {headers: headers});
  },
  
  async updateGlobalConfiguration(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_APP_CONFIGURATION;
    let headers = this.requestHeaders(authUser);
    headers['Content-Type'] = 'application/json';
    
    return axios.put(url, data, {headers: headers});
  },

  async updateCompanyConfiguration(authUser, data) {
    let url = Vue.WyConstants.SERVER.ACCESS_HOST + this.API.MANAGEMENT_APP_CONFIGURATION;
    let headers = this.requestHeaders(authUser);
    headers['Content-Type'] = 'application/json';

    return axios.put(url, data, {headers: headers});
  },

  fetchCompanyProfileSettings(authUser) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_PROFILE;
    return this.getRequest(authUser, url);
  },

  fetchCompanyConfigSettings(authUser, data) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_CONFIG;
    return this.postRequest(authUser, url, data);
  },

  updateCompanyProfileSettings(authUser, data) {    
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_PROFILE_UPDATE;
    return this.postRequest(authUser, url, data);
  },

  updateCompanyConfigSettings(authUser, data) {    
    let url = Vue.WyConstants.SERVER.INTERACTIONS_HOST + this.API.UPDATE_COMPANY_CONFIG_SETTING;
    return this.postRequest(authUser, url, data);
  },

  fetchCompanyAlertSettings(authUser) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_ALERT_SETTINGS;
    return this.getRequest(authUser, url);
  },

  updateCompanyAlertSettings(authUser, data) {    
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_ALERT_SETTINGS_UPDATE;
    return this.postRequest(authUser, url, data);
  },

  resetAlert(authUser) {    
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_ALERT_RESET;
    return this.getRequest(authUser, url);
  },

  fetchCompanyPromptSettings (authUser) {
    const url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_PROMPT;
    return this.getRequest(authUser, url);
  },

  updateCompanyPromptSettings(authUser, data) {    
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_PROMPT_UPDATE;
    return this.postRequest(authUser, url, data);
  },

  // new global and company configurations in OC backend
  async fetchGlobalConfigurations() {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_GLOBAL_CONFIGURATION;
    let headers = {
      "'Content-Type'": "application/json"
    };
    return axios.get(url, {headers: headers});
  },

  // new global and company configurations in OC backend
  async fetchCompanyGlobalConfigurations(authUser) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_GLOBAL_CONFIGURATION;
    return this.getRequest(authUser, url);
  },

  async fetchCompanyConfigurations(authUser) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_CONFIGURATION;
    return this.getRequest(authUser, url);
  },

  async updateGlobalConfigurations(authUser, data) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_GLOBAL_CONFIGURATION_UPDATE;
    return this.postRequest(authUser, url, data);
  },

  async updateCompanyConfigurations(authUser, data) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.SETTINGS_COMPANY_CONFIGURATION_UPDATE;
    return this.postRequest(authUser, url, data);
  },

  async loginC1(data) {
    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.C1_Login;
    let res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'manual',
      body: JSON.stringify(data)
    });

    return {
      status: res.status,
      data: await res.json()
    };
  },

  async authC1Token(authHost, authOptions, sessionId, productName) {
    const data = {
      host: authHost,
      sid: sessionId,
      options: authOptions,
      productName: productName
    }

    let url = Vue.WyConstants.SERVER.ANALYTICS_HOST + this.API.C1_AUTH_TOKEN;
    let res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'manual',
      body: JSON.stringify(data)
    });

    if(res.body != null && res.body != undefined) {
      try {
        res.data = await res.json();
      }
      catch(e) {
        console.log(e);
      }
    }

    if(res.status != 200) {
      if(res.data && res.data.errorCode) {
        return res;
      }
      else {
        return {status: 401, data: {errorCode: 5002}};
      }
    }
    return res;
  },
}